import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState, type ButtonHTMLAttributes } from "react";
import { useQuery } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import {
  fetchCategories,
  toMarketGroup,
  keys,
  FEED_MARKETS_PREVIEW_LIMIT,
  type MarketGroup,
} from "@/lib/queries";
import { useEventsFeed } from "@/lib/useEventsFeed";
import { useChannelTickers } from "@/hooks/useChannelTickers";
import { useUrlState } from "@/lib/useUrlState";
import { Chip, Skeleton, EmptyState } from "@/components/ui";
import { useInfiniteScroll } from "@/lib/useInfiniteScroll";
import { TopBarControls, TopBarTitle } from "@/components/layout/ContentTopBar";
import { MarketCard, type TickerFor } from "@/components/markets/MarketCard";
import { MarketCardSkeleton } from "@/components/markets/MarketCardSkeleton";
import { groupResolution } from "@/lib/outcomes";
import { cn } from "@/lib/cn";
import { Compass, Inbox, Search, X } from "@/icons";
import { buildHead } from "@/lib/seo";

const PAGE = 12;

export const Route = createFileRoute("/_app/explore")({
  head: () =>
    buildHead({
      path: "/explore",
      title: "Explore markets",
      description:
        "Browse every open market by category — sports, politics, crypto, entertainment and more.",
    }),
  validateSearch: (s: Record<string, unknown>): { cat?: string; q?: string } => ({
    cat: typeof s.cat === "string" ? s.cat : undefined,
    q: typeof s.q === "string" ? s.q : undefined,
  }),
  component: ExplorePage,
});

// Categories carry no colour, so derive a stable tile colour by index to match
// the .fig category grid. Literal rgba (not theme tokens) — these are decorative
// per-category brand tints, the muted ~0.4-alpha-over-dark palette straight from
// SmartMarket.fig (first 6) extended with same-style hues for longer catalogs.
const TILE = [
  "rgba(219,21,140,0.40)", // pink
  "rgba(132,0,231,0.40)", // purple
  "rgba(31,51,99,0.42)", // blue
  "rgba(95,129,9,0.42)", // green
  "rgba(71,125,150,0.42)", // teal
  "rgba(228,78,45,0.40)", // orange
  "rgba(124,58,237,0.40)", // violet
  "rgba(178,80,24,0.40)", // amber
  "rgba(37,99,235,0.40)", // azure
  "rgba(21,128,61,0.40)", // emerald
];

function ExplorePage() {
  const { t } = useTranslation("discover");
  const navigate = useNavigate();
  const { q = "" } = Route.useSearch();
  const [cat, setCat] = useUrlState("cat", "", { replace: false });
  // Local input value for responsiveness; the committed `q` (in the URL) drives
  // the feed query. Keep them in sync when q changes via back/forward or a
  // result's Back link.
  const [qInput, setQInput] = useState(q);
  useEffect(() => setQInput(q), [q]);

  // Commit the search box into the URL — stays on /explore (preserving the
  // selected category) and drives the feed query, so search never bounces to
  // Home. Fires on submit (Enter) and debounced as the user types.
  const commitSearch = useCallback(
    (v: string) => navigate({ to: "/explore", search: (prev) => ({ ...prev, q: v.trim() || undefined }) }),
    [navigate],
  );
  useEffect(() => {
    if (qInput.trim() === q.trim()) return;
    const id = window.setTimeout(() => commitSearch(qInput), 300);
    return () => window.clearTimeout(id);
  }, [commitSearch, q, qInput]);
  // Clear both the query and the category in a single navigation.
  const clearAll = useCallback(() => navigate({ to: "/explore", search: {} }), [navigate]);

  const categoriesQ = useQuery({ queryKey: keys.categories(), queryFn: () => fetchCategories({ limit: 60, offset: 0 }) });
  const cats = categoriesQ.data?.items ?? [];
  const catLookup = useMemo(() => new Map(cats.map((c) => [c.category_id, c])), [cats]);

  // Feed: rising-topic preview in the default view, or the filtered list once a
  // category and/or a search query is active. Paged by offset (one PAGE per
  // scroll) via useEventsFeed so a long category never ships in one fetch;
  // changing cat/q swaps the cache key, resetting the feed to page 0.
  // Fetch the full outcome set per event (not just 2 by display_order) so each
  // card ranks by live Yes ask and previews the true top 2 — the same order the
  // detail page shows inside the event.
  const feed = useEventsFeed(
    { q: q || undefined, category_id: cat || undefined, marketsLimit: FEED_MARKETS_PREVIEW_LIMIT },
    PAGE,
  );
  const groups: MarketGroup[] = useMemo(
    () =>
      feed.events
        .map((e) => {
          const c = catLookup.get(e.category_id);
          return toMarketGroup(e, c?.name, c?.icon_url);
        })
        .filter((g) => g.markets.length > 0)
        // Unresolved events first; fully-resolved ones sink (stable sort keeps
        // server order within each bucket). Also keeps the featured card live.
        .sort((a, b) => Number(groupResolution(a.markets).allResolved) - Number(groupResolution(b.markets).allResolved)),
    [feed.events, catLookup],
  );
  // The discovery view's Rising-Topic carousel features the top trending events.
  const risingGroups = useMemo(() => groups.slice(0, 6), [groups]);
  const ids = useMemo(() => groups.flatMap((g) => g.markets.map((m) => m.market_id)), [groups]);
  // Live tickers for every visible market, read from the ACTIVE MODE's books
  // (SM-483/SM-466). This page fetched and subscribed in LOGICAL space until
  // now — and a logical id IS the CASH channel id, so in GAME mode the whole
  // discovery surface (Rising Topic carousel, All Markets grid, category and
  // search lists) priced itself from CASH books under a GAME label. Every
  // other grid moved onto this hook; explore was missed.
  //
  // keepPrevious holds the prior grid's prices while a widened id set is in
  // flight, which is what category swaps and infinite scroll do here.
  const { tickerFor } = useChannelTickers(ids, { refetchInterval: 30_000, keepPrevious: true });

  const catName = cat ? catLookup.get(cat)?.name ?? t("category") : "";
  const searching = q.trim().length > 0;
  const total = groups.length;
  // List view = a category is selected OR there's a search query; otherwise the
  // discovery view (rising topic + category grid).
  const listMode = !!cat || searching;
  // Both the discovery "All Markets" grid and the list view page the same feed,
  // so load-more is enabled in either mode (only one sentinel is mounted at a
  // time, so the single ref is unambiguous).
  const hasMore = feed.hasNextPage;
  const loadMoreRef = useInfiniteScroll(feed.loadMore, hasMore && !feed.isFetchingNextPage);

  return (
    <div className="-mt-4 lg:-mx-6">
      {/* Sticky page header — incoming standardized bar (TopBarTitle + search +
          TopBarControls). In list mode the category filter bar drops below it. */}
      {/* Opaque bg (was bg-bg/95 + backdrop-blur): the blur was barely visible
          at 95% and a blurred sticky bar adds a faint shimmer on scroll. */}
      <div data-topbar className="sticky top-0 z-20 border-b border-border bg-bg">
        <div className={cn("flex flex-col items-start justify-center px-4 lg:px-6", listMode ? "gap-3 pb-3 pt-2" : "h-[74px] py-2")}>
          <div className={cn("flex min-w-0 flex-1 items-center gap-3", listMode ? "w-full" : "h-[58px] w-full")}>
            {/* Compass (not a magnifier) for the page title — the search field
                right beside it already carries the search icon, so a Search icon
                here read as a duplicate search affordance. */}
            <TopBarTitle icon={<Compass className="h-6 w-6 shrink-0 text-fg" />}>{t("common:nav.explore")}</TopBarTitle>
            <form
              className="relative min-w-0 flex-1"
              onSubmit={(e) => {
                e.preventDefault();
                commitSearch(qInput);
              }}
            >
              <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-fg-muted" />
              <input
                type="search"
                value={qInput}
                onChange={(e) => setQInput(e.target.value)}
                placeholder={t("explore.searchPlaceholder")}
                aria-label={t("explore.searchAnything")}
                // [&::-webkit-search-cancel-button]:hidden — type="search" draws a
                // native clear (✕); hide it so only our custom button shows (no double ✕).
                className="h-[46px] w-full rounded-[40px] border-0 bg-input pl-11 pr-10 font-display text-sm font-normal leading-5 text-fg outline-none placeholder:text-fg-muted/50 focus-visible:ring-2 focus-visible:ring-ring/45 [&::-webkit-search-cancel-button]:hidden"
              />
              {qInput && (
                <button
                  type="button"
                  aria-label={t("explore.clearSearch")}
                  onClick={() => {
                    setQInput("");
                    commitSearch("");
                  }}
                  className="absolute right-4 top-1/2 grid h-5 w-5 -translate-y-1/2 place-items-center rounded text-fg-muted transition-colors hover:text-fg"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </form>
            <TopBarControls className="h-full" />
          </div>
          {listMode && <CategoryFilterBar cats={cats} activeCat={cat} onSelect={setCat} />}
        </div>
      </div>

      {/* content column — full width so the All-Markets grid can span 4 cols on
          desktop; the list (category/search) view re-caps itself to the Figma
          feed width below. */}
      <div className="w-full">
        {!listMode ? (
          <div>
            {/* Rising Topic + Categories.
                Mobile & tablet (<xl): stacked full-width — Rising Topic (a 2-up
                carousel from sm) above the Categories grid, so neither is squeezed
                into a half-column on iPad. Desktop (xl+): side by side, Rising
                Topic on the left, Categories on the right. */}
            <div className="flex flex-col gap-5 p-5 xl:flex-row xl:items-start">
              {/* Rising topic — swipeable carousel of the top trending events */}
              <section className="min-w-0 flex-1 xl:w-1/2 xl:flex-none">
                <h2 className="mb-5 font-display text-base font-medium leading-6 text-fg">{t("explore.risingTopic")}</h2>
                {feed.isLoading ? (
                  <MarketCardSkeleton imageLed />
                ) : risingGroups.length > 0 ? (
                  <RisingTopicCarousel groups={risingGroups} tickerFor={tickerFor} />
                ) : (
                  <EmptyState icon={<Inbox />} title={t("explore.noTrendingMarkets")} description={t("checkBackSoon")} />
                )}
              </section>

              {/* All categories — fixed-size colour tiles, wrapping left-to-right.
                  Right-hand column on desktop (xl+); full-width below Rising Topic
                  on mobile and tablet. */}
              <section className="w-full shrink-0 xl:w-1/2">
                <h2 className="mb-5 font-display text-base font-medium leading-6 text-fg">{t("explore.allCategories")}</h2>
                {categoriesQ.isError ? (
                  <EmptyState icon={<Compass />} title={t("explore.couldntLoadCategories")} description={t("catalogUnreachable")} />
                ) : categoriesQ.isLoading ? (
                  <div className="flex flex-wrap gap-x-6 gap-y-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <Skeleton key={i} className="h-[90px] w-[calc(50%-12px)] rounded-[13px] sm:w-[164px]" />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-x-6 gap-y-4">
                    {cats.map((c, i) => (
                      <CategoryTile
                        key={c.category_id}
                        name={c.name}
                        iconUrl={c.icon_url}
                        color={TILE[i % TILE.length]}
                        onClick={() => setCat(c.category_id)}
                      />
                    ))}
                  </div>
                )}
              </section>
            </div>

            {/* All markets uses the same responsive image-led grid as Home:
                one wide card through tablet, two on desktop, and four on wide desktop. */}
            <section className="px-5 pb-5 pt-3">
              <h2 className="mb-5 font-display text-base font-medium leading-6 text-fg">{t("explore.allMarkets")}</h2>
              {feed.isLoading ? (
                <div className="grid grid-cols-1 gap-3 xl:grid-cols-2 xl:gap-4 2xl:grid-cols-4">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <MarketCardSkeleton key={i} imageLed />
                  ))}
                </div>
              ) : groups.length === 0 ? (
                <EmptyState icon={<Inbox />} title={t("explore.noMarketsYet")} description={t("checkBackSoon")} />
              ) : (
                <>
                  <div className="grid grid-cols-1 gap-3 xl:grid-cols-2 xl:gap-4 2xl:grid-cols-4">
                    {groups.map((g) => (
                      <MarketCard key={g.id} group={g} tickerFor={tickerFor} linkTo="/explore/$id" imageLed />
                    ))}
                  </div>
                  {hasMore && (
                    <div ref={loadMoreRef} className="mt-3 lg:mt-4">
                      {feed.isFetchingNextPage && (
                        <div className="grid grid-cols-1 gap-3 xl:grid-cols-2 xl:gap-4 2xl:grid-cols-4">
                          {Array.from({ length: 4 }).map((_, i) => (
                            <MarketCardSkeleton key={i} imageLed />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </section>
          </div>
        ) : (
          <div className="p-5">
            <div className="mb-4 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm lg:max-w-[623px]">
              <span className="text-fg-muted">{total}</span>
              {/* Trans keys carry the word order per language; the styled spans
                  come from the components map (muted label vs bold query/cat).
                  count drives plural selection only — the number renders in the
                  span above, outside the key. */}
              {searching ? (
                <Trans
                  t={t}
                  i18nKey={cat ? "explore.resultsForIn" : "explore.resultsFor"}
                  count={total}
                  values={{ query: q, category: catName }}
                  components={{
                    muted: <span className="text-fg-muted" />,
                    q: <span className="font-semibold" />,
                  }}
                />
              ) : (
                <Trans
                  t={t}
                  i18nKey="explore.topicsIn"
                  count={total}
                  values={{ category: catName }}
                  components={{
                    muted: <span className="text-fg-muted" />,
                    cat: <span className="font-semibold" />,
                  }}
                />
              )}
            </div>
            {feed.isLoading ? (
              <div className="space-y-3 lg:max-w-[623px]">
                {Array.from({ length: 4 }).map((_, i) => (
                  <MarketCardSkeleton key={i} imageLed />
                ))}
              </div>
            ) : groups.length === 0 ? (
              // Empty state breaks out of the 623px feed column so the box sits
              // centred in the full content area rather than pinned to the left.
              <div className="flex min-h-[50vh] items-center justify-center">
                <EmptyState
                  icon={<Inbox />}
                  title={searching ? t("explore.noResults") : t("explore.noMarketsHereYet")}
                  description={searching ? t("tryDifferentSearch") : t("explore.tryAnotherCategory")}
                  action={
                    <Chip selected onClick={clearAll}>
                      {t("clearFilters")}
                    </Chip>
                  }
                  className="w-full max-w-md"
                />
              </div>
            ) : (
              <div className="lg:max-w-[623px]">
                <div className="space-y-3">
                  {groups.map((g) => (
                    <MarketCard key={g.id} group={g} tickerFor={tickerFor} linkTo="/explore/$id" cat={cat || undefined} q={q || undefined} imageLed />
                  ))}
                </div>
                {hasMore && (
                  <div ref={loadMoreRef} className="mt-3">
                    {feed.isFetchingNextPage && (
                      <div className="space-y-3">
                        {Array.from({ length: 3 }).map((_, i) => (
                          <MarketCardSkeleton key={i} imageLed />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Top trending events as a one-card-per-view, scroll-snap carousel with the
// .fig pagination dots (active = elongated pill). Swipe on touch, click a dot on
// desktop; the active index tracks whichever card is centred in the viewport.
function RisingTopicCarousel({ groups, tickerFor }: { groups: MarketGroup[]; tickerFor: TickerFor }) {
  const { t } = useTranslation("discover");
  const ref = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);

  // The card whose left edge is nearest the container's start wins. This matches
  // the slides' snap-start alignment and stays unambiguous whether one card
  // fills the view (mobile/desktop) or two are visible (tablet 2-up) — a
  // nearest-to-centre test would tie between the pair straddling the centre.
  // It's also consistent with goTo, which snaps a card's left edge to the start.
  // Measured via getBoundingClientRect (viewport-relative, scroll-aware) rather
  // than offsetLeft, which is relative to the static offsetParent (the body).
  const onScroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const start = el.getBoundingClientRect().left;
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < el.children.length; i++) {
      const r = (el.children[i] as HTMLElement).getBoundingClientRect();
      const dist = Math.abs(r.left - start);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    setActive(best);
  }, []);

  const goTo = useCallback((i: number) => {
    const el = ref.current;
    const child = el?.children[i] as HTMLElement | undefined;
    if (!el || !child) return;
    const left = el.scrollLeft + (child.getBoundingClientRect().left - el.getBoundingClientRect().left);
    el.scrollTo({ left, behavior: "smooth" });
  }, []);

  return (
    <div className="space-y-2">
      <div
        ref={ref}
        onScroll={onScroll}
        className="flex snap-x snap-mandatory gap-3 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {groups.map((g) => (
          // 1 card per view on mobile; 2-up on tablet (the section spans full
          // width there); back to 1 on desktop where Rising Topic returns to a
          // half-width column. snap-start so the 2-up pair aligns to the edge.
          // calc(50%-6px) + the gap-3 (12px) makes exactly two cards fill a row.
          <div key={g.id} className="w-full shrink-0 snap-start sm:w-[calc(50%-6px)] lg:w-full">
            <MarketCard group={g} tickerFor={tickerFor} linkTo="/explore/$id" imageLed />
          </div>
        ))}
      </div>
      {groups.length > 1 && (
        <div className="flex items-center justify-center gap-2">
          {groups.map((g, i) => (
            <button
              key={g.id}
              type="button"
              aria-label={t("explore.goToTopic", { number: i + 1 })}
              aria-current={i === active}
              onClick={() => goTo(i)}
              className={cn(
                "h-2 rounded-full transition-all",
                i === active ? "w-6 bg-surface-3" : "w-2 bg-surface hover:bg-fg-subtle",
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryFilterBar({
  cats,
  activeCat,
  onSelect,
}: {
  cats: { category_id: string; name: string }[];
  activeCat: string;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation("discover");
  return (
    <div className="flex w-full gap-2 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <CategoryFilterChip selected={!activeCat} onClick={() => onSelect("")}>
        {t("explore.all")}
      </CategoryFilterChip>
      {cats.map((c) => (
        <CategoryFilterChip key={c.category_id} selected={activeCat === c.category_id} onClick={() => onSelect(c.category_id)}>
          {c.name}
        </CategoryFilterChip>
      ))}
    </div>
  );
}

function CategoryFilterChip({
  selected,
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { selected?: boolean }) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      className={cn(
        "flex shrink-0 items-center justify-center rounded-[12px] px-4 py-2 font-display text-sm font-medium leading-5 transition-colors",
        selected
          ? "bg-primary text-primary-fg"
          : "border border-surface-2 text-fg-muted hover:border-border-strong hover:bg-surface hover:text-fg",
        className,
      )}
      {...props}
    />
  );
}

// A single category tile from the .fig: fixed 164×90 on desktop, icon above
// label with the exact 20px internal gap, on a muted brand tint.
function CategoryTile({
  name,
  iconUrl,
  color,
  onClick,
}: {
  name: string;
  iconUrl?: string | null;
  color: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ backgroundColor: color }}
      className="flex h-[90px] w-[calc(50%-12px)] flex-col items-start justify-center overflow-hidden rounded-[13px] px-[10px] pb-[14px] pt-[10px] text-left text-white transition-transform hover:scale-[1.02] sm:w-[164px]"
    >
      <span className="flex flex-col items-start justify-center gap-5">
        {iconUrl ? (
          <img src={iconUrl} alt="" className="h-5 w-5 shrink-0 object-contain" />
        ) : (
          <CategoryShortcutIcon className="h-5 w-5 shrink-0" />
        )}
        <span className="line-clamp-1 font-display text-sm font-semibold leading-5 text-fg">{name}</span>
      </span>
    </button>
  );
}

function CategoryShortcutIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="none" aria-hidden>
      <path
        d="M4.15 4.4C5.6 3.18 7.69 2.5 10 2.5c4.14 0 7.5 2.46 7.5 5.5s-3.36 5.5-7.5 5.5c-.66 0-1.31-.06-1.92-.19l-3.66 2.22a.62.62 0 0 1-.93-.63l.66-3.18C3.11 10.75 2.5 9.47 2.5 8c0-1.36.59-2.61 1.65-3.6Z"
        fill="currentColor"
      />
    </svg>
  );
}
